/**
 * Tracks navigation context before opening a PDP.
 *
 * Shopify only exposes the last collection on a product URL — not the menu path
 * the visitor actually followed (e.g. Home > Geurstokjes > sub-collection).
 * We persist the menu trail and last browsed page in sessionStorage.
 */
(function () {
  const PARENT_STORAGE_KEY = 'theme:pdp-breadcrumb-parent';
  const TRAIL_STORAGE_KEY = 'theme:pdp-breadcrumb-trail';

  function isProductPath(pathname) {
    return /\/products(?:\/|$)/.test(pathname);
  }

  function isCollectionPath(pathname) {
    return /\/collections(?:\/|$)/.test(pathname);
  }

  function normalizePath(path) {
    if (!path) return '';

    try {
      return new URL(path, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (error) {
      return path.replace(/\/+$/, '') || '/';
    }
  }

  function readStorage(key) {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Safari private mode and similar environments may block storage.
    }
  }

  function removeStorage(key) {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      // Ignore storage errors.
    }
  }

  function dedupeTrail(items) {
    const seen = new Set();
    const result = [];

    for (const item of items) {
      if (!item?.url || !item?.title) continue;

      const path = normalizePath(item.url);
      if (path === '/' || seen.has(path)) continue;

      seen.add(path);
      result.push({ url: item.url, title: item.title });
    }

    return result;
  }

  function getPageNavTrail() {
    const trail = window.themeVariables?.breadcrumbNavTrail;
    return Array.isArray(trail) ? trail : [];
  }

  function getPageParent() {
    const parent = window.themeVariables?.breadcrumbParent;
    if (!parent?.url || !parent?.title) return null;

    const path = normalizePath(parent.url);
    if (path === '/' || isProductPath(path)) return null;

    return parent;
  }

  function getFallbackPageParent() {
    const path = normalizePath(window.location.pathname);
    if (path === '/' || isProductPath(path)) return null;

    const title = document.querySelector('main h1, h1')?.textContent?.trim()
      || document.title.split(/\s[–-]\s/)[0]?.trim();

    if (!title) return null;

    return { url: path, title };
  }

  function persistBrowsingContext() {
    if (window.themeVariables?.settings?.pageType === 'product') return;

    const serverTrail = getPageNavTrail();
    const storedTrail = readStorage(TRAIL_STORAGE_KEY) || [];
    const pageParent = getPageParent() || getFallbackPageParent();

    if (serverTrail.length > 0) {
      writeStorage(TRAIL_STORAGE_KEY, dedupeTrail(serverTrail));
    } else if (pageParent && isCollectionPath(normalizePath(pageParent.url))) {
      const path = normalizePath(pageParent.url);
      const alreadyInTrail = storedTrail.some((item) => normalizePath(item.url) === path);

      if (!alreadyInTrail) {
        writeStorage(TRAIL_STORAGE_KEY, dedupeTrail([...storedTrail, pageParent]));
      }
    }

    if (pageParent) {
      writeStorage(PARENT_STORAGE_KEY, pageParent);
    }
  }

  function storeContextForProductNavigation() {
    if (window.themeVariables?.settings?.pageType === 'product') return;

    persistBrowsingContext();
  }

  function storeContextForCollectionNavigation(link) {
    if (window.themeVariables?.settings?.pageType === 'product') return;

    const href = link.getAttribute('href');
    const title = link.textContent?.trim();
    if (!href || !title) return;

    const path = normalizePath(href);
    if (!isCollectionPath(path)) return;

    const serverTrail = getPageNavTrail();
    const storedTrail = readStorage(TRAIL_STORAGE_KEY) || [];
    const pageParent = getPageParent();
    let trail = serverTrail.length > 0 ? serverTrail : storedTrail;

    if (pageParent) {
      const pagePath = normalizePath(pageParent.url);
      const targetPath = path;

      if (pagePath !== targetPath && !trail.some((item) => normalizePath(item.url) === pagePath)) {
        trail = [...trail, pageParent];
      }
    }

    writeStorage(TRAIL_STORAGE_KEY, dedupeTrail(trail));
    writeStorage(PARENT_STORAGE_KEY, { url: href, title });
  }

  function createBreadcrumbItem({ url, title }, position) {
    const item = document.createElement('li');
    item.className = 'breadcrumbs__item breadcrumbs__item--trail';
    item.setAttribute('itemprop', 'itemListElement');
    item.setAttribute('itemscope', '');
    item.setAttribute('itemtype', 'https://schema.org/ListItem');

    item.innerHTML = `
      <span class="breadcrumbs__separator" aria-hidden="true">-</span>
      <a href="${url.replace(/"/g, '&quot;')}" class="breadcrumbs__link link-faded" itemprop="item">
        <span itemprop="name">${title.replace(/</g, '&lt;')}</span>
      </a>
      <meta itemprop="position" content="${position}">
    `;

    return item;
  }

  function applyProductBreadcrumb() {
    if (window.themeVariables?.settings?.pageType !== 'product') return;

    const list = document.querySelector('.breadcrumbs__list');
    const currentItem = document.querySelector('.breadcrumbs__item--current');
    if (!list || !currentItem) return;

    const storedTrail = readStorage(TRAIL_STORAGE_KEY) || [];
    const storedParent = readStorage(PARENT_STORAGE_KEY);
    const hasStoredContext = storedTrail.length > 0 || storedParent?.url;

    if (!hasStoredContext) return;

    document.querySelectorAll('[data-breadcrumb-nav-level], [data-breadcrumb-parent]').forEach((item) => {
      item.hidden = true;
    });

    const crumbs = dedupeTrail([
      ...storedTrail,
      ...(storedParent ? [storedParent] : [])
    ]);

    let position = 2;

    for (const crumb of crumbs) {
      list.insertBefore(createBreadcrumbItem(crumb, position), currentItem);
      position += 1;
    }

    const meta = currentItem.querySelector('meta[itemprop="position"]');
    if (meta) meta.setAttribute('content', String(position));

    removeStorage(TRAIL_STORAGE_KEY);
    removeStorage(PARENT_STORAGE_KEY);
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank') return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      if (href.includes('/products/')) {
        storeContextForProductNavigation();
        return;
      }

      if (href.includes('/collections/') && !href.includes('/products/')) {
        storeContextForCollectionNavigation(link);
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      persistBrowsingContext();
      applyProductBreadcrumb();
    });
  } else {
    persistBrowsingContext();
    applyProductBreadcrumb();
  }
})();
